"use strict";
// Copyright (C) Microsoft Corporation. All rights reserved.
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPersistentFocus = exports.handlePersistentFocusChange = exports.PersistentFocus = exports.usePersistentFocusRef = exports.InnerPersistentFocus = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importStar(require("react"));
const focusManager_1 = require("../core/managers/focusManager");
class InnerPersistentFocus extends react_1.Component {
    constructor(props) {
        super(props);
        this.doesChildHaveFocus = () => this.childHasFocus;
        this.setChildHasFocus = (hasFocus) => {
            this.childHasFocus = hasFocus;
        };
        this.getElement = () => {
            return this.props.contentRef.current;
        };
        this.childHasFocus = false;
        this.props.persistentFocusRef.current = this;
        registerPersistentFocus(this, props.contentRef);
    }
    componentWillUnmount() {
        unregisterPersistentFocus(this);
    }
    render() {
        return react_1.default.createElement(react_1.default.Fragment, null, this.props.children);
    }
}
exports.InnerPersistentFocus = InnerPersistentFocus;
/**
 * Hook to tap into the `PersistentFocus`'s ref context.
 * Use this hook when you're NOT passing your own `contentRef` to `PersistentFocus`.
 *
 * This hook provides two values:
 * - `ref`: A ref object that points to the currently focused element. This is useful for accessing the ref directly.
 * - `setRef`: A function to update the ref. For `PersistentFocus` to work as intended, you MUST pass this function
 *   as the ref prop of the child component intended to be focused inside `PersistentFocus`.
 *
 * Ensure you call this hook within the child components of `PersistentFocus` to avoid React context-related issues.
 *
 * ```tsx
 * const { ref, setRef } = usePersistentFocusRef();
 * <SomeComponent ref={setRef} />
 * ```
 *
 * @throws Will throw an error if used outside the context of `PersistentFocus` without an explicit `contentRef`.
 *
 * @returns An object with the `ref` and `setRef` values.
 */
const usePersistentFocusRef = () => {
    const contextValue = (0, react_1.useContext)(PersistentFocusContext);
    if (!contextValue) {
        throw new Error('usePersistentFocusRef can only be used if you are NOT passing in your own `contentRef` to PersistentFocus.');
    }
    return contextValue;
};
exports.usePersistentFocusRef = usePersistentFocusRef;
const PersistentFocusContext = (0, react_1.createContext)(null);
/**
 * `PersistentFocus` is a component that maintains focus persistence for its children.
 * It offers two distinct usages:
 *
 * 1. **Explicit Ref Management (contentRef provided)**:
 *    For scenarios where the prop at the level of `PersistentFocus` directly affects which element within `PersistentFocus`
 *    is focusable. In such cases, by passing `contentRef`, you indicate that you are managing ref yourself and any changes to
 *    `PersistentFocus` are tracked by observing changes to this ref.
 *
 * ```tsx
 * const myContentRef = useRef<HTMLElement | null>(null);
 *
 * <PersistentFocus contentRef={myContentRef}>
 *    <MyComponent ref={myContentRef} />
 * </PersistentFocus>
 * ```
 *
 * 2. **Automatic Ref Management (contentRef not provided)**:
 *    For scenarios where the child component of `PersistentFocus` manages which element is focusable. In this setup,
 *    `PersistentFocus` uses an internal ref and provides it via a context. However, you MUST ensure `usePersistentFocusRef`
 *    is called within the child components of `PersistentFocus` and not before the render function of `PersistentFocus` itself.
 *    This is due to the React context limitation, where if called too early, it wouldn't find the `PersistentFocusContext`.
 *
 * ```tsx
 *
 * <PersistentFocus>
 *    <MyComponent />
 * </PersistentFocus>
 *
 * const MyComponent = () => {
 *    const { ref, setRef } = usePersistentFocusRef();
 *
 *    return (
 *         <SomethingElse ref={setRef} />
 *     );
 * }
 * ```
 *
 * @param contentRef - Optional ref object. If you're observing focus changes at the `PersistentFocus` level, pass this.
 * Otherwise, ensure the child component taps into the ref via `usePersistentFocusRef`.
 * @param deps - Optional DependencyList. The `PersistentFocus` will only update focus on changes to the given dependencies.
 * If you're observing focus change at the `PersistentFocus` level and want to avoid the library performing unnecessary checks
 * each re-render, pass this.
 * @param children - React children.
 *
 * @returns A React element with focus persistence capabilities.
 */
const PersistentFocus = ({ contentRef, children, deps }) => {
    const persistentFocusRef = (0, react_1.useRef)(null);
    const internalRef = (0, react_1.useRef)(null);
    const effectiveRef = contentRef || internalRef;
    const refocus = (0, react_1.useCallback)(() => {
        var _a;
        if (effectiveRef.current && ((_a = persistentFocusRef.current) === null || _a === void 0 ? void 0 : _a.doesChildHaveFocus())) {
            (0, focusManager_1.setCurrentFocus)(effectiveRef.current, false, false);
        }
    }, [effectiveRef, persistentFocusRef]);
    const setRef = (0, react_1.useCallback)((node) => {
        effectiveRef.current = node;
        refocus();
    }, [effectiveRef, refocus]);
    (0, react_1.useEffect)(() => {
        refocus();
    }, deps);
    (0, react_1.useEffect)(() => {
        if (persistentFocusRef.current) {
            if (contentRef) {
                registerContentRef(persistentFocusRef.current, contentRef);
            }
            else {
                registerContentRef(persistentFocusRef.current, internalRef);
            }
        }
    }, [contentRef]);
    if (contentRef) {
        return (react_1.default.createElement(InnerPersistentFocus, { persistentFocusRef: persistentFocusRef, contentRef: contentRef }, children));
    }
    else {
        return (react_1.default.createElement(PersistentFocusContext.Provider, { value: { ref: internalRef, setRef } },
            react_1.default.createElement(InnerPersistentFocus, { persistentFocusRef: persistentFocusRef, contentRef: internalRef }, children)));
    }
};
exports.PersistentFocus = PersistentFocus;
// Mapping of PersistentFocus to their respective ref elements
const persistentFocusIdMap = new Map();
// Registers a PersistentFocus to map it and its underlying components
const registerPersistentFocus = (persistentFocus, contentRef) => {
    persistentFocusIdMap.set(persistentFocus, contentRef);
};
// When unmounted, remove references to the PersistentFocus for cleanup
const unregisterPersistentFocus = (persistentFocus) => {
    persistentFocusIdMap.delete(persistentFocus);
};
const registerContentRef = (persistentFocus, contentRef) => {
    persistentFocusIdMap.set(persistentFocus, contentRef);
    if (contentRef.current && persistentFocus.doesChildHaveFocus()) {
        (0, focusManager_1.setCurrentFocus)(contentRef.current, false, false);
    }
};
const handlePersistentFocusChange = (previousElement, nextElement) => {
    const previousPersistentFocus = (0, exports.getPersistentFocus)(previousElement);
    const nextPersistentFocus = (0, exports.getPersistentFocus)(nextElement);
    if (previousPersistentFocus !== nextPersistentFocus) {
        previousPersistentFocus === null || previousPersistentFocus === void 0 ? void 0 : previousPersistentFocus.setChildHasFocus(false);
        nextPersistentFocus === null || nextPersistentFocus === void 0 ? void 0 : nextPersistentFocus.setChildHasFocus(true);
    }
};
exports.handlePersistentFocusChange = handlePersistentFocusChange;
const getPersistentFocus = (element) => {
    if (!element) {
        return;
    }
    for (const [persistentFocus, contentRef] of persistentFocusIdMap.entries()) {
        if (contentRef.current === element) {
            return persistentFocus;
        }
    }
    return;
};
exports.getPersistentFocus = getPersistentFocus;
//# sourceMappingURL=data:application/json;base64,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